import { Track } from "@/src/lib/tracking";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash-es/debounce";
import { pubsub } from "utils";
import { useSnapshot } from "valtio";
import {
  CATEGORY_LIMITS,
  CATEGORY_TITLES,
  CategoryInfo,
  CategoryType,
  ProcessedSearchData,
  SearchState,
  ViewState,
} from "./types";
import {
  OpenDataTypeEnum,
  SearchOpenData,
  SearchOpenDataGroup,
} from "@features/api/openData";

import { favoritesCenter } from "@store/favorite";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { rootStore } from "@store/rootStore";
import { useSearchGroup } from "@/src/hooks/useSearchGroup";

/**
 * 主题关键词匹配函数
 * 只对value字段进行匹配，不做关键字toLowerCase转换
 */

/**
 * 判断分类是否应该显示
 */
const shouldShowCategory = (results: any[]): boolean => {
  return results && results.length > 0;
};

/**
 * 判断是否显示 View All 按钮
 */
const shouldShowViewAll = (
  totalCount: number,
  category: CategoryType,
): boolean => {
  return totalCount > CATEGORY_LIMITS[category];
};

/**
 * MenuSearchModal 状态管理 Hook
 */
export const useMenuSearchModal = (
  _type?: "normal" | "portfolio",
  onModalClose?: () => void,
  defaultRecommendedTab: "stock" | "crypto" = "stock",
) => {
  // 统一的滚动容器引用 - 所有视图共用，避免 DOM 重建导致 ref 失效
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);

  // 搜索状态
  const [searchState, setSearchState] = useState<SearchState>({
    viewState: ViewState.RECOMMENDED,
    keywords: "",
    selectedCategory: undefined,
    categoryTitle: undefined,
    recommendedTab: defaultRecommendedTab, // 默认选中 tab，可由外部指定
  });

  // 防抖后的搜索关键词状态
  const [debouncedKeywords, setDebouncedKeywords] = useState<string>("");

  // 创建防抖函数 - 300ms延迟
  const debouncedSetKeywords = useMemo(
    () =>
      debounce((keywords: string) => {
        setDebouncedKeywords(keywords);
      }, 200),
    [],
  );

  // 监听搜索关键词变化，触发防抖
  useEffect(() => {
    debouncedSetKeywords(searchState.keywords);

    // 清理函数
    return () => {
      debouncedSetKeywords.cancel();
    };
  }, [searchState.keywords, debouncedSetKeywords]);

  // 数据获取 - 使用防抖后的关键词调用 useSearchGroup Hook
  const { data: searchResults, isLoading } = useSearchGroup({
    keywords: debouncedKeywords,
    type: _type,
  });

  // 滚动到顶部的功能
  // 使用自定义动画实现更流畅的滚动体验
  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      const element = scrollContainerRef.current;
      const startPosition = element.scrollTop;
      const targetPosition = 0;
      const distance = startPosition - targetPosition;
      const duration = 200; // 200ms 更快的滚动动画
      let startTime: number | null = null;

      const animation = (currentTime: number) => {
        if (startTime === null) {
          startTime = currentTime;
        }
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);

        // 使用 easeOut 缓动函数，让滚动更流畅
        const easeOut = 1 - Math.pow(1 - progress, 3);
        element.scrollTop = startPosition - distance * easeOut;

        if (progress < 1) {
          requestAnimationFrame(animation);
        }
      };
      requestAnimationFrame(animation);
    } else {
      Track.log("error", "MenuSearchModal", "❌ 滚动容器引用为空！");
    }
  }, []); // 无需依赖任何状态，统一容器始终存在

  // 数据处理 - 将API结果和主题筛选结果合并
  const processedData = useMemo((): ProcessedSearchData | null => {
    if (!debouncedKeywords) {
      return null;
    }

    const categorizedData: ProcessedSearchData = {
      stockThemes: [],
      cryptoThemes: [],
      stocks: [],
      crypto: [],
      news: [],
    };

    // 处理API搜索结果
    // 调试日志：检查API响应结构

    if (searchResults && Array.isArray(searchResults)) {
      searchResults?.forEach((group: any) => {
        switch (group.type) {
          case OpenDataTypeEnum.STOCK:
            categorizedData.stocks = group.rows || [];
            break;
          case OpenDataTypeEnum.TOKEN:
            categorizedData.crypto = group.rows || [];
            break;
          case OpenDataTypeEnum.CONTENT:
            categorizedData.news = group.rows || [];
            break;
        }
      });
    } else {
    }

    const stockThemes = searchResults
      ?.filter(
        (theme: SearchOpenDataGroup) => theme.type === OpenDataTypeEnum.THEME,
      )
      ?.flatMap((theme) => theme.rows)
      ?.filter(
        (item): item is SearchOpenData =>
          item?.sub_types !== undefined &&
          item?.sub_types.includes(OpenDataTypeEnum.STOCK),
      );

    const cryptoThemes = searchResults
      ?.filter(
        (theme: SearchOpenDataGroup) => theme.type === OpenDataTypeEnum.THEME,
      )
      ?.flatMap((theme) => theme.rows)
      ?.filter(
        (item): item is SearchOpenData =>
          item?.sub_types !== undefined &&
          item?.sub_types.includes(OpenDataTypeEnum.TOKEN),
      );
    categorizedData.stockThemes = stockThemes ?? [];
    categorizedData.cryptoThemes = cryptoThemes ?? [];
    return categorizedData;
  }, [searchResults, debouncedKeywords]);

  useEffect(() => {
    if (!processedData?.stocks?.length && !processedData?.crypto?.length) {
      return;
    }
    const StocksIds = processedData?.stocks?.map((item) => item.id);
    const CryptoIds = processedData?.crypto?.map((item) => item.id);
    if (isLoggedIn && ownerInfo?.id) {
      favoritesCenter.checkBatchFavoritesStatus([...CryptoIds], "TOKEN");
      favoritesCenter.checkBatchFavoritesStatus([...StocksIds], "STOCK");
    }
  }, [processedData, isLoggedIn, ownerInfo?.id]);

  // 获取要显示的分类列表（搜索概览用）
  const visibleCategories = useMemo((): CategoryInfo[] => {
    if (!processedData) {
      return [];
    }

    const categories: CategoryInfo[] = [];

    // Stock Themes - 前端筛选结果
    if (shouldShowCategory(processedData.stockThemes) && _type === "normal") {
      categories.push({
        type: CategoryType.STOCK_THEMES,
        title: CATEGORY_TITLES[CategoryType.STOCK_THEMES],
        data: processedData.stockThemes.slice(
          0,
          CATEGORY_LIMITS[CategoryType.STOCK_THEMES],
        ),
        total: processedData.stockThemes.length,
        showViewAll: shouldShowViewAll(
          processedData.stockThemes.length,
          CategoryType.STOCK_THEMES,
        ),
      });
    }

    // Crypto Themes - 前端筛选结果
    if (shouldShowCategory(processedData.cryptoThemes) && _type === "normal") {
      categories.push({
        type: CategoryType.CRYPTO_THEMES,
        title: CATEGORY_TITLES[CategoryType.CRYPTO_THEMES],
        data: processedData.cryptoThemes.slice(
          0,
          CATEGORY_LIMITS[CategoryType.CRYPTO_THEMES],
        ),
        total: processedData.cryptoThemes.length,
        showViewAll: shouldShowViewAll(
          processedData.cryptoThemes.length,
          CategoryType.CRYPTO_THEMES,
        ),
      });
    }

    // Stocks - API搜索结果
    const filteredStocks = processedData.stocks;
    if (shouldShowCategory(filteredStocks)) {
      categories.push({
        type: CategoryType.STOCKS,
        title: CATEGORY_TITLES[CategoryType.STOCKS],
        data: filteredStocks.slice(0, CATEGORY_LIMITS[CategoryType.STOCKS]),
        total: filteredStocks.length,
        showViewAll: shouldShowViewAll(
          filteredStocks.length,
          CategoryType.STOCKS,
        ),
      });
    }

    // Crypto - API搜索结果
    const filteredCrypto = processedData.crypto;
    if (shouldShowCategory(filteredCrypto)) {
      categories.push({
        type: CategoryType.CRYPTO,
        title: CATEGORY_TITLES[CategoryType.CRYPTO],
        data: filteredCrypto.slice(0, CATEGORY_LIMITS[CategoryType.CRYPTO]),
        total: filteredCrypto.length,
        showViewAll: shouldShowViewAll(
          filteredCrypto.length,
          CategoryType.CRYPTO,
        ),
      });
    }

    // News - API搜索结果
    if (shouldShowCategory(processedData.news)) {
      categories.push({
        type: CategoryType.NEWS,
        title: CATEGORY_TITLES[CategoryType.NEWS],
        data: processedData.news.slice(0, CATEGORY_LIMITS[CategoryType.NEWS]),
        total: processedData.news.length,
        showViewAll: shouldShowViewAll(
          processedData.news.length,
          CategoryType.NEWS,
        ),
      });
    }
    return categories;
  }, [processedData, _type]);

  // 获取单个分类的完整数据（分类详细页面用）
  const getCategoryDetailData = useCallback(
    (categoryType: CategoryType) => {
      if (!processedData) {
        return null;
      }

      switch (categoryType) {
        case CategoryType.STOCK_THEMES:
          return {
            type: CategoryType.STOCK_THEMES,
            title: CATEGORY_TITLES[CategoryType.STOCK_THEMES],
            data: processedData.stockThemes,
            total: processedData.stockThemes.length,
            showViewAll: false,
          };
        case CategoryType.CRYPTO_THEMES:
          return {
            type: CategoryType.CRYPTO_THEMES,
            title: CATEGORY_TITLES[CategoryType.CRYPTO_THEMES],
            data: processedData.cryptoThemes,
            total: processedData.cryptoThemes.length,
            showViewAll: false,
          };
        case CategoryType.STOCKS:
          return {
            type: CategoryType.STOCKS,
            title: CATEGORY_TITLES[CategoryType.STOCKS],
            data: processedData.stocks,
            total: processedData.stocks.length,
            showViewAll: false,
          };
        case CategoryType.CRYPTO:
          return {
            type: CategoryType.CRYPTO,
            title: CATEGORY_TITLES[CategoryType.CRYPTO],
            data: processedData.crypto,
            total: processedData.crypto.length,
            showViewAll: false,
          };
        case CategoryType.NEWS:
          return {
            type: CategoryType.NEWS,
            title: CATEGORY_TITLES[CategoryType.NEWS],
            data: processedData.news,
            total: processedData.news.length,
            showViewAll: false,
          };
        default:
          return null;
      }
    },
    [processedData],
  );

  // 搜索输入处理
  const handleSearchInput = useCallback(
    (value: string) => {
      const newViewState = value.trim()
        ? ViewState.SEARCH_OVERVIEW
        : ViewState.RECOMMENDED;

      setSearchState((prev) => ({
        ...prev,
        keywords: value,
        viewState: newViewState,
        selectedCategory: undefined,
        categoryTitle: undefined,
      }));

      // 如果切换到搜索概览，滚动到顶部
      if (newViewState === ViewState.SEARCH_OVERVIEW) {
        // 等待内容完全渲染后再滚动
        setTimeout(scrollToTop, 50); // 延迟确保DOM更新完成
      }
    },
    [scrollToTop],
  );

  // View All 按钮点击处理
  const handleViewAll = useCallback(
    (category: CategoryType, title: string) => {
      setSearchState((prev) => ({
        ...prev,
        viewState: ViewState.CATEGORY_DETAIL,
        selectedCategory: category,
        categoryTitle: title,
      }));

      // 切换到分类详细页面时，滚动到顶部
      setTimeout(scrollToTop, 50); // 延迟确保DOM更新完成
    },
    [scrollToTop],
  );

  // 返回按钮处理
  const handleBack = useCallback(() => {
    setSearchState((prev) => ({
      ...prev,
      viewState: ViewState.SEARCH_OVERVIEW,
      selectedCategory: undefined,
      categoryTitle: undefined,
    }));

    // 返回到搜索概览时，滚动到顶部
    setTimeout(scrollToTop, 50); // 延迟确保DOM更新完成
  }, [scrollToTop]);

  // 关闭模态框处理
  const handleClose = useCallback(() => {
    // setSearchState({
    //   viewState: ViewState.RECOMMENDED,
    //   keywords: "",
    //   selectedCategory: undefined,
    //   categoryTitle: undefined,
    //   recommendedTab: "stock",
    // });
  }, []);

  // 推荐区域 tab 切换处理
  const handleRecommendedTabChange = useCallback((tab: "stock" | "crypto") => {
    setSearchState((prev) => ({
      ...prev,
      recommendedTab: tab,
    }));
  }, []);

  // 计算当前视图状态
  const currentView = useMemo(
    () => ({
      showRecommended: searchState.viewState === ViewState.RECOMMENDED,
      showSearchOverview: searchState.viewState === ViewState.SEARCH_OVERVIEW,
      showCategoryDetail: searchState.viewState === ViewState.CATEGORY_DETAIL,
    }),
    [searchState.viewState],
  );

  // 是否有搜索结果
  const hasSearchResults = useMemo(() => {
    return visibleCategories.length > 0;
  }, [visibleCategories]);

  // 监听登录成功事件，自动关闭弹窗
  useEffect(() => {
    const handleCloseSearchModal = () => {
      Track.log(
        "info",
        "MenuSearchModal",
        "检测到CLOSE_SEARCH_MODAL事件，准备关闭弹窗",
      );
      // 先清理内部状态
      handleClose();
      // 再调用外部的关闭回调，真正关闭弹窗
      if (onModalClose) {
        Track.log("info", "MenuSearchModal", "调用外部关闭回调");
        onModalClose();
      }
    };

    // pubsub.on() 返回一个取消订阅的函数
    const unsubscribe = pubsub.on(
      pubSubKeyMap.CLOSE_SEARCH_MODAL,
      handleCloseSearchModal,
    );

    return () => {
      unsubscribe();
    };
  }, [handleClose, onModalClose]);

  return {
    searchState,
    processedData,
    visibleCategories,
    currentView,
    isLoading,
    hasSearchResults,
    getCategoryDetailData,
    // 导出统一的滚动容器引用
    scrollContainerRef,
    handleSearchInput,
    handleViewAll,
    handleBack,
    handleClose,
    handleRecommendedTabChange,
  };
};
